import { gql, useMutation } from '@apollo/client';
import { lazy, Suspense, use, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router';
import { PrivateRoute } from '../components/PrivateRoute';
import { resetAnalyticsKeepingConsent } from '../analytics/consent';
import { ZendeskMessengerApi } from '../components/ZendeskWidget/zendesk';
import { fetchAccessToken, getAccessToken, setAccessToken, setManagerAccessToken } from '../components/accesstoken';
import { consumeSkipAutoLogin, setExpectedUserId } from '../components/sessionIdentity';
import Candidate from '../components/candidate/Candidate';
import AccountInformation from '../components/elements/AccountInformation';
import LoadingScreen from '../components/elements/Loading';
import type { SetUserVariables, UserContext } from '../components/hooks/LocaleContext';
import LocaleContext, { SessionState } from '../components/hooks/LocaleContext';
import KanbanBoard from '../components/kanban/KanbanBoard';
import Home from '../components/pages/Home';
import { IframePage } from '../components/pages/IframePage';
import { Jobs } from '../components/pages/Jobs';
import OpenApplicationContainer from '../components/pages/OpenApplicationContainer';
import { SmartRecruitersConsent } from '../components/pages/SmartRecruitersConsent';
import Swiper from '../components/pages/Swiper';
import AdminLogin from '../components/pages/fullpage/AdminLogin';
import Error404 from '../components/pages/fullpage/404';
import Forgot from '../components/pages/fullpage/Forgot';
import SetNewPassword from '../components/pages/fullpage/SetNewPassword';
import SignIn from '../components/pages/fullpage/SignIn';
import Signup from '../components/pages/fullpage/Signup';
import Talentech from '../components/pages/fullpage/Talentech';
import Zendesk from '../components/pages/fullpage/Zendesk';
import AccessGroups from '../components/settings/AccessGroup';
import AccessGroupsV2 from '../components/settings/AccessGroupv2';
import Integration from '../components/settings/Integration';
import LeverAuth from '../components/settings/Integrations/Lever/leverAuth';
import Organization from '../components/settings/Organization';
import Security from '../components/settings/Security';
import Settings from '../components/settings/SettingsPage';
import UserProfile from '../components/settings/UserProfile';
import { TermsModal } from '../components/terms/TermsModal';
import { config } from '../config';
// import { initPendo } from '../utils/initPendo';
import DashboardLayout from './DashboardLayout';

const Statistics = lazy(() => import('../components/statistics/Statistics'));
const BhWidget = lazy(() => import('../components/pages/BullhornWidget'));
const BhrcWidget = lazy(() => import('../components/pages/BhrcWidget'));
const MsfWidget = lazy(() => import('../components/pages/MsfWidget'));
const CampaignList = lazy(() => import('../components/pages/outreach/CampaignList'));
const CreateCampaign = lazy(() => import('../components/pages/outreach/CreateCampaign'));
const CampaignDetails = lazy(() => import('../components/pages/outreach/CampaignDetails'));
const TemplateList = lazy(() => import('../components/pages/outreach/TemplateList'));
const TemplateEditor = lazy(() => import('../components/pages/outreach/TemplateEditor'));

const CandidateDetails = lazy(() => import('../components/pages/outreach/CandidateDetails'));

const LOGOUT_MUTATION = gql`
  mutation LogoutMutation($logout: String!) {
    logoutResolverMutation(logout: $logout)
  }
`;

const pickDefined = <T extends object>(x: T): Partial<T> => {
  if (!x || typeof x !== 'object') return x;
  return Object.fromEntries(
    Object.entries(x)
      .filter(([_, v]) => v !== undefined)
      .map(([k, v]): [string, unknown] => [k, pickDefined(v)]),
  ) as Partial<T>;
};

const hasKey = (x: object, k: PropertyKey): x is { [k]: unknown } => k in x;

const _merge = (x: object | null, y: object | null) => {
  if (!x) return y;
  if (!y) return x;

  for (const [k, v] of Object.entries(y))
    Reflect.set(x, k, typeof v === 'object' && hasKey(x, k) && typeof x[k] === 'object' ? _merge(x[k], v) : v);
  return x;
};

const merge = (x: object, y: object | null) => _merge(structuredClone(x), structuredClone(y));

const MainLayout = () => {
  const { userSettings, setLoggedIn } = use(LocaleContext);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const bootstrap = consumeSkipAutoLogin() ? Promise.resolve({ accessToken: '' }) : fetchAccessToken();

    bootstrap
      .then(({ accessToken: token }) => {
        if (token) {
          setAccessToken(token);
          setLoggedIn?.(true);
          setLoading(false);
        } else {
          setAccessToken('');
          setManagerAccessToken('');
          setLoggedIn?.(false);
          setLoading(false);
        }
      })
      .catch((err) => {
        setAccessToken('');
        setManagerAccessToken('');
        setLoggedIn?.(false);
        setLoading(false);
        console.error(err);
      });
  }, [setLoggedIn]);

  return (
    <div className="layout">
      <LoadingScreen show={loading} />
      {userSettings.state !== undefined && <Outlet />}
    </div>
  );
};

const Layout = () => {
  const navigate = useNavigate();

  const [userSettings, setUserSettings] = useState<{
    state?: SessionState;
    loggedIn?: boolean;
    settings: {
      anonymized: boolean;
      showPassed: boolean;
    };
    canActAs: [];
  }>({
    state: undefined,
    loggedIn: undefined,
    settings: {
      anonymized: false,
      showPassed: false,
    },
    canActAs: [],
  });

  const setUserVariables = useCallback(({ user }: Parameters<SetUserVariables>[0]) => {
    setUserSettings((prev) => {
      const newUserSettings = merge(prev, pickDefined(user)) as typeof userSettings;
      console.info('setUserSettings', { ...newUserSettings });
      return newUserSettings;
    });
  }, []);

  const setState = useCallback(
    (state: SessionState) =>
      setUserSettings((prev) => ({
        ...prev,
        loggedIn: state === SessionState.LoggedIn,
        state: state,
      })),
    [],
  );

  const doLogOut = () => {
    setAccessToken('');
    setState(SessionState.LoggingOut);
  };

  const [logoutMutation, { client }] = useMutation(LOGOUT_MUTATION, {
    variables: { logout: 'true' },
    onCompleted() {
      sessionStorage.clear();
      client.clearStore().then(() => {
        client.resetStore();
      });
      doLogOut();
      navigate('/signin', { replace: true });
    },
  });

  const logout = useCallback(() => {
    ZendeskMessengerApi.logout();
    // Forget the analytics identity but keep the recruiter's consent choice.
    resetAnalyticsKeepingConsent();
    logoutMutation();
  }, [logoutMutation]);

  const setLoggedIn = useCallback(
    (loggedIn: boolean) => {
      setState(loggedIn ? SessionState.LoggedIn : SessionState.LoggedOut);
    },
    [setState],
  );

  const switchTo = useCallback(
    (id: string) => async () => {
      const r = await fetch(`${config.VITE_GRAPHQL_URL}/api/users/switch-to`, {
        body: JSON.stringify({ id }),
        method: 'POST',
        credentials: 'include',
        headers: {
          authorization: `bearer ${getAccessToken()}`,
          'content-type': 'application/json',
        },
      });
      if (!r.ok) throw new Error(`Switching organization failed with status ${r.status}`);
      const data = await r.json();
      setAccessToken(data.accessToken);
      setExpectedUserId(data.accessToken);
      await fetchAccessToken();
      setUserSettings((x) => ({ ...x, canActAs: [] }));
      await client.resetStore();
    },
    [client, setLoggedIn],
  );

  const userValues = useMemo<UserContext>(
    () => ({
      userSettings,
      setLoggedIn,
      setUserVariables,
      switchTo,
      logout,
    }),
    [userSettings, logout, setLoggedIn, setUserVariables, switchTo],
  );

  // useEffect(() => {
  //   const id = userValues.userSettings?.id;
  //   if (!id) return;

  //   initPendo(id);
  // }, [userValues.userSettings?.id]);

  return (
    <LocaleContext value={userValues}>
      <Routes>
        <Route path="/application/share/:id" element={<OpenApplicationContainer />} />
        <Route path="/" element={<MainLayout />}>
          <Route path="signin" element={<SignIn />} />
          <Route
            path="signin/hubert"
            element={
              <Navigate to={`/signin?pid=${config.DEV ? 'saml.hubert-provrum' : 'saml.hubert-app'}&auto=1`} replace />
            }
          />
          <Route
            path="signin/resourcebank"
            element={<Navigate to={`/signin?pid=saml.resourcebank&auto=1`} replace />}
          />
          <Route path="signin/sweco" element={<Navigate to={`/signin?pid=saml.sweco&auto=1`} replace />} />
          <Route
            path="signin/tp-greece"
            element={<Navigate to={`/signin?pid=saml.teleperformance-gr&auto=1`} replace />}
          />
          <Route path="forgot" element={<Forgot />} />
          <Route path="invitation" element={<Signup />} />
          <Route path="setpassword" element={<SetNewPassword />} />
          <Route path="admin-login" element={<AdminLogin />} />
          <Route path="error" element={<Error404 />} />
          <Route element={<PrivateRoute />}>
            <Route path="help/login" element={<Zendesk />} />
            <Route path="auth/talentech" element={<Talentech />} />
            <Route path="lever/login" element={<LeverAuth />} />
            <Route
              path="BhWidget"
              element={
                <Suspense>
                  <BhWidget />
                </Suspense>
              }
            />
            <Route
              path="BhrcWidget"
              element={
                <Suspense>
                  <BhrcWidget />
                </Suspense>
              }
            />
            <Route
              path="MsfWidget"
              element={
                <Suspense>
                  <MsfWidget />
                </Suspense>
              }
            />
            {userSettings.state !== SessionState.LoggingOut && (
              <Route element={<DashboardLayout />}>
                <Route index element={<Home />} />
                <Route path="terms" element={<TermsModal setUserVariables={setUserVariables!} />} />
                <Route path="job">
                  <Route path=":id" element={<KanbanBoard />} />
                  <Route
                    path=":id/statistics"
                    element={
                      <Suspense>
                        <Statistics />
                      </Suspense>
                    }
                  />
                  <Route path=":id/:appId" element={<Swiper />} />
                </Route>
                <Route
                  path="outreach/campaigns"
                  element={
                    <Suspense>
                      <CampaignList />
                    </Suspense>
                  }
                />
                <Route
                  path="outreach/campaigns/new"
                  element={
                    <Suspense>
                      <CreateCampaign />
                    </Suspense>
                  }
                />
                <Route
                  path="outreach/campaigns/:id/edit"
                  element={
                    <Suspense>
                      <CreateCampaign />
                    </Suspense>
                  }
                />
                <Route
                  path="outreach/campaigns/:id"
                  element={
                    <Suspense>
                      <CampaignDetails />
                    </Suspense>
                  }
                />
                <Route
                  path="outreach/campaigns/:id/candidates/:candidateId"
                  element={
                    <Suspense>
                      <CandidateDetails />
                    </Suspense>
                  }
                />
                <Route
                  path="outreach/templates"
                  element={
                    <Suspense>
                      <TemplateList />
                    </Suspense>
                  }
                />
                <Route
                  path="outreach/templates/new"
                  element={
                    <Suspense>
                      <TemplateEditor />
                    </Suspense>
                  }
                />
                <Route
                  path="outreach/templates/:id/edit"
                  element={
                    <Suspense>
                      <TemplateEditor />
                    </Suspense>
                  }
                />
                <Route path="jobs" element={<Jobs />} />
                <Route path="questions/*" element={<IframePage key="questions" />} />
                <Route path="templates/*" element={<IframePage key="templates" />} />
                <Route path="agent-chat/*" element={<IframePage key="agent-chat" />} />
                <Route path="candidates">
                  <Route path=":candidateId/" element={<Candidate />} />
                  <Route path=":candidateId/:applicationId" element={<Candidate />} />
                </Route>
                <Route path="settings" element={<Settings />}>
                  <Route index element={<UserProfile />} />
                  <Route path="profile" element={<UserProfile />} />
                  <Route path="security" element={<Security />} />
                  <Route path="organization" element={<Organization />} />
                  <Route path="accessgroups" element={<AccessGroups />} />
                  <Route path="accessgroupsv2" element={<AccessGroupsV2 />} />
                  <Route path="accountinfo" element={<AccountInformation />} />
                  <Route path="integrations" element={<Integration />} />
                </Route>
                <Route path="integrations/smartrecruiters/consent" element={<SmartRecruitersConsent />} />
              </Route>
            )}
          </Route>
        </Route>
      </Routes>
    </LocaleContext>
  );
};

export default Layout;
